/**
 * Workflow History List Component
 * Displays a list of workflow runs with expandable details
 */

import React, { useState, useCallback } from 'react';
import { Card, Button, Stack, Flex, useTheme, spacing, fontSize } from '@fti/design-system';
import { StatusBadge } from './StatusBadge';
import type { EchoRun } from '../../api/types/echo';

export interface WorkflowHistoryListProps {
  runs: EchoRun[];
  isLoading?: boolean;
  showUserColumn?: boolean;
  onViewDetails?: (run: EchoRun) => void;
}

/**
 * List component for displaying workflow history
 */
export function WorkflowHistoryList({
  runs,
  isLoading = false,
  showUserColumn = false,
  onViewDetails,
}: WorkflowHistoryListProps) {
  const { theme } = useTheme();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const toggleRow = useCallback((runId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  }, []);

  const formatDate = (dateString: string) => {
    // Backend returns UTC timestamps without Z suffix — append it so JS parses as UTC
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    const formatted = date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${formatted} (EST)`;
  };

  // Loading state
  if (isLoading) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <p style={{ color: mutedColor }}>Loading workflow history...</p>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  // Empty state
  if (runs.length === 0) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <Stack gap="sm" align="center">
              <span style={{ fontSize: fontSize['2xl'] }}>📋</span>
              <p style={{ margin: 0, color: mutedColor }}>No workflow runs found</p>
            </Stack>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  // Build table columns based on EchoRun type
  const columns = [
    { key: 'expand', label: '', width: '40px' },
    { key: 'status', label: 'Status', width: '120px' },
    { key: 'runId', label: 'Run ID', width: '80px' },
    ...(showUserColumn ? [{ key: 'user', label: 'User', width: '1fr' }] : []),
    { key: 'created', label: 'Created', width: '180px' },
    { key: 'actions', label: '', width: '100px' },
  ];

  return (
    <div
      style={{
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Table Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: columns.map((c) => c.width).join(' '),
          background: theme.colors.background.paper,
          borderBottom: `1px solid ${theme.colors.border.default}`,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.xs,
          fontWeight: 600,
          color: mutedColor,
          textTransform: 'uppercase',
        }}
      >
        {columns.map((col) => (
          <div key={col.key}>{col.label}</div>
        ))}
      </div>

      {/* Table Rows */}
      {runs.map((run) => {
        const runIdStr = String(run.run_id);
        const isExpanded = expandedRows.has(runIdStr);
        return (
          <div
            key={runIdStr}
            style={{
              borderBottom: `1px solid ${theme.colors.border.default}`,
            }}
          >
            {/* Main Row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: columns.map((c) => c.width).join(' '),
                padding: `${spacing.sm} ${spacing.md}`,
                alignItems: 'center',
                fontSize: fontSize.sm,
              }}
            >
              {/* Expand Button */}
              <div>
                <button
                  onClick={() => toggleRow(runIdStr)}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: mutedColor,
                  }}
                >
                  {isExpanded ? '▼' : '▶'}
                </button>
              </div>

              {/* Status */}
              <div>
                <StatusBadge status={run.status} size="sm" />
              </div>

              {/* Run ID */}
              <div style={{ color: textColor, fontWeight: 500 }}>
                #{run.run_id}
              </div>

              {/* User (optional) */}
              {showUserColumn && (
                <div style={{ color: mutedColor, fontSize: fontSize.sm, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {run.account_id || '-'}
                </div>
              )}

              {/* Created */}
              <div style={{ color: mutedColor, fontSize: fontSize.sm }}>
                {formatDate(run.created_at)}
              </div>

              {/* Actions */}
              <div>
                {onViewDetails && run.status?.toLowerCase() === 'completed' && (
                  <Button
                    variant="outline"
                    color="primary"
                    size="sm"
                    onClick={() => onViewDetails(run)}
                  >
                    View
                  </Button>
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div
                style={{
                  padding: `${spacing.md} ${spacing.lg}`,
                  background: theme.colors.background.paper,
                  borderTop: `1px solid ${theme.colors.border.default}`,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: spacing.md,
                    fontSize: fontSize.sm,
                  }}
                >
                  <Stack gap="xs">
                    <strong style={{ color: textColor }}>Run Details</strong>
                    <div>
                      <span style={{ color: mutedColor }}>Run ID: </span>
                      <span style={{ color: textColor }}>{run.run_id}</span>
                    </div>
                    <div>
                      <span style={{ color: mutedColor }}>Streamer ID: </span>
                      <span style={{ color: textColor }}>{run.echo_streamer_id}</span>
                    </div>
                    <div>
                      <span style={{ color: mutedColor }}>User: </span>
                      <span style={{ color: textColor }}>{run.account_id}</span>
                    </div>
                  </Stack>

                  <Stack gap="xs">
                    <strong style={{ color: textColor }}>Billing</strong>
                    {run.video_length != null && (
                      <div>
                        <span style={{ color: mutedColor }}>Duration: </span>
                        <span style={{ color: textColor }}>{run.video_length} min</span>
                      </div>
                    )}
                    {run.billing_rate != null && (
                      <div>
                        <span style={{ color: mutedColor }}>Rate: </span>
                        <span style={{ color: textColor }}>${run.billing_rate}/min</span>
                      </div>
                    )}
                    {run.run_cost != null && (
                      <div>
                        <span style={{ color: mutedColor }}>Cost: </span>
                        <span style={{ color: textColor }}>${run.run_cost.toFixed(2)}</span>
                      </div>
                    )}
                    {run.s3_storage_location && (
                      <div>
                        <span style={{ color: mutedColor }}>Storage: </span>
                        <span
                          style={{
                            color: theme.colors.primary[600],
                            wordBreak: 'break-all',
                            fontSize: fontSize.xs,
                          }}
                        >
                          {run.s3_storage_location}
                        </span>
                      </div>
                    )}
                  </Stack>
                </div>

                {run.prompt && (
                  <div style={{ marginTop: spacing.md }}>
                    <strong style={{ color: textColor }}>Prompt</strong>
                    <div
                      style={{
                        marginTop: spacing.xs,
                        padding: spacing.sm,
                        background: theme.colors.background.default,
                        borderRadius: '4px',
                        fontSize: fontSize.sm,
                        color: mutedColor,
                        maxHeight: '100px',
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {run.prompt}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default WorkflowHistoryList;
